import React, { useCallback, useEffect, useState } from "react";
import { Button, H3, MultiSelectField, SystemMessage, Text } from "@shoptet/ui";
import { flatten, uniq } from "lodash";
import { Trans, useTranslation } from "react-i18next";
import {
  AdSystemT,
  ProductExclusionQueryT,
  useExcludeCategoriesMutation,
  useExcludeProductFlagsMutation,
  useProductExclusionQuery,
} from "../../graphql/generated/graphql";
import { CategoryTree } from "../CategoriesSelection";
import { CheckmarkIcon } from "../Icons";
import { ModalWrapper } from "../ModalWrapper";
import ProductsDashboard from "../ProductsDashboard/ProductsDashboard";
import { SmallLoader } from "../SmallLoader";

type ExcludeByFlagPropsT = {
  adSystem?: AdSystemT;
  loading: boolean;
  onHasUnsavedChanges?: (hasUnsaved: boolean) => void;
  onOpenModal: () => void;
  organization?: NonNullable<ProductExclusionQueryT["organization"]>;
  selectedIds: string[];
  setSelectedIds: (v: string[]) => void;
};

type LabelOption = {
  label: string;
  value: string;
};

const ExcludeByFlag = ({
  adSystem = AdSystemT.GoogleAdsT,
  loading,
  onHasUnsavedChanges,
  onOpenModal,
  organization,
  selectedIds,
  setSelectedIds,
}: ExcludeByFlagPropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.AdvancedSetup" });
  const [isSaved, setIsSaved] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [isFlagsInfoModalOpen, setIsFlagsInfoModalOpen] = useState(false);

  const [excludeProductFlags, { error: mutationError, loading: mutationLoading }] = useExcludeProductFlagsMutation({
    refetchQueries: ["ProductExclusion"],
    onCompleted: () => {
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
      setTimeout(() => setIsUpdated(false), 2000);
    },
  });

  useEffect(() => {
    onHasUnsavedChanges?.(isUpdated);
  }, [isUpdated, onHasUnsavedChanges]);

  if (loading || !organization) {
    return <SmallLoader />;
  }

  const options = organization.productFlags.reduce(
    (acc, { id, name, productsCount }) => ({ ...acc, [id]: `${name} (${productsCount} ${t("products")})` }),
    { "": "" }
  );

  const mappedOptions = Object.entries(options)
    .filter(([value, label]) => Boolean(value && label))
    .map(([value, label]) => ({ value, label }));

  const lastProductsToExclude = selectedIds.length >= mappedOptions.length;

  return (
    <div className="">
      <Text>
        <Trans
          i18nKey="Exclude by product flags Text"
          t={t}
          components={{
            a: (
              <a
                onClick={(event) => {
                  event.preventDefault();
                  setIsFlagsInfoModalOpen(true);
                }}
              />
            ),
            b: <b />,
          }}
        />
      </Text>
      <Text>
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            onOpenModal();
          }}
        >
          {t("When exclude products tooltip label")}
        </a>
      </Text>
      {mutationError && <SystemMessage description={t("there was a problem saving excluded flags")} level="error" />}
      <div style={{ display: "flex", flexDirection: "row", gap: "16px", alignItems: "center", marginTop: "16px" }}>
        <div data-test-id="exclude_flags">
          <MultiSelectField<LabelOption>
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            label={t("Exclude products label")}
            options={mappedOptions}
            value={selectedIds}
            onChange={(value) => {
              setSelectedIds(value);
              setIsUpdated(true);
            }}
          />
        </div>
        {isUpdated && (
          <span className="addon" style={{ display: "flex", gap: "8px" }}>
            <Button
              data-test-id="save-excluded-products"
              disabled={mutationLoading || isSaved || lastProductsToExclude}
              variant="action"
              onClick={() =>
                excludeProductFlags({
                  variables: { productFlagIds: selectedIds, adSystem },
                })
              }
            >
              {(isSaved && (
                <span style={{ display: "flex", alignItems: "center" }}>
                  <CheckmarkIcon /> &nbsp; {t("Saved")}
                </span>
              )) ||
                (mutationLoading && t("Saving")) ||
                t("Save")}
            </Button>
            <Button
              iconLeft="shp-undo"
              variant="secondary"
              onClick={() => {
                const savedIds =
                  organization?.productFlags
                    .filter((flag) => (adSystem === AdSystemT.MetaAdsT ? flag.excludedMetaAds : flag.excludedGoogleAds))
                    .map(({ id }) => id) || [];
                setSelectedIds(savedIds);
                setIsUpdated(false);
              }}
            >
              {t("Reset to default")}
            </Button>
          </span>
        )}
      </div>
      {lastProductsToExclude && <SystemMessage description={t("You cannot exclude all products")} level="error" />}
      <ModalWrapper
        isOpen={isFlagsInfoModalOpen}
        title={t("Flags info modal title")}
        maxWidth
        onClose={() => setIsFlagsInfoModalOpen(false)}
      >
        <div style={{ marginBottom: "20px" }}>
          <Text>
            <Trans components={{ b: <b /> }} i18nKey="Flags info modal text 1" t={t} />
          </Text>
        </div>
        <Text>
          <Trans components={{ b: <b /> }} i18nKey="Flags info modal text 2" t={t} />
        </Text>
      </ModalWrapper>
    </div>
  );
};

type ExcludeCategoriesSelectionPropsT = {
  adSystem?: AdSystemT;
  categories?: NonNullable<ProductExclusionQueryT["organization"]>["categories"];
  disabled?: boolean;
  onHasUnsavedChanges?: (hasUnsaved: boolean) => void;
  onOpenModal: () => void;
};

const returnCategoryTreeIds = (
  parentId: string,
  categories?: NonNullable<ProductExclusionQueryT["organization"]>["categories"]
): string[] => {
  const cat = (categories || []).find((category) => category.id === parentId);
  if (!cat) {
    return [parentId];
  }
  return flatten([
    parentId,
    ...(categories || [])
      .filter((category) => category.parentGuid === cat.guid)
      .map((category) => returnCategoryTreeIds(category.id, categories)),
  ]);
};

const ExcludeCategoriesSelection = ({
  adSystem = AdSystemT.GoogleAdsT,
  categories,
  disabled,
  onHasUnsavedChanges,
  onOpenModal,
}: ExcludeCategoriesSelectionPropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Wizard Suggestion Generator Page" });
  const [isSaved, setIsSaved] = useState(false);

  const getExcludedField = (category: NonNullable<ProductExclusionQueryT["organization"]>["categories"][number]) => {
    return adSystem === AdSystemT.MetaAdsT ? category.excludedMetaAds : category.excludedGoogleAds;
  };

  const [selectedCategoryIds, setSelectedCategoryIds] = useState(
    (categories || []).filter((category) => getExcludedField(category)).map(({ id }) => id)
  );
  const currentSelectedIds = (categories || []).filter((category) => getExcludedField(category)).map(({ id }) => id);

  const [selectCategories, { error: mutationError, loading: mutationLoading }] = useExcludeCategoriesMutation({
    variables: { categoryIds: selectedCategoryIds, adSystem },
    refetchQueries: ["ProductExclusion"],
    onCompleted: () => {
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
    },
  });

  const addCategory = useCallback(
    (id: string) => {
      const newState = uniq([...selectedCategoryIds, ...returnCategoryTreeIds(id, categories)]);
      setSelectedCategoryIds(newState);
    },
    [setSelectedCategoryIds, selectedCategoryIds, categories]
  );
  const removeCategory = useCallback(
    (id: string) => {
      const removeIds = returnCategoryTreeIds(id, categories);
      const newState = [...selectedCategoryIds].filter((catId) => removeIds.indexOf(catId) === -1);
      setSelectedCategoryIds(newState);
    },
    [setSelectedCategoryIds, selectedCategoryIds, categories]
  );

  const hasUnsavedCategoryChanges =
    JSON.stringify(currentSelectedIds.sort()) !== JSON.stringify(selectedCategoryIds.sort());

  useEffect(() => {
    onHasUnsavedChanges?.(hasUnsavedCategoryChanges);
  }, [hasUnsavedCategoryChanges, onHasUnsavedChanges]);

  const subCategories = (categories || []).filter((category) => category.parentGuid !== null);
  const allSubCategoriesSelected =
    !!subCategories.length && subCategories.every((category) => selectedCategoryIds.includes(category.id));
  const selectedAllCategories = selectedCategoryIds.length === categories?.length;
  return (
    <>
      <Text>
        <Trans components={{ b: <b /> }} i18nKey="Exclude by categories Text" t={t} />
      </Text>
      <br />
      <Text>
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            onOpenModal();
          }}
        >
          {t("When exclude products tooltip label")}
        </a>
      </Text>
      <div data-test-id="category-exclusion-default-category-note" style={{ margin: "16px 0" }}>
        <SystemMessage
          level="notice"
          description={
            <Text>
              <Trans components={{ b: <b /> }} i18nKey="Exclude by categories default category note" t={t} />
            </Text>
          }
        />
      </div>
      <div
        style={{
          border: "1px solid var(--app-border)",
          borderRadius: "var(--app-radius-box)",
          margin: "8px 0 0",
          padding: "16px 24px",
        }}
      >
        <CategoryTree
          addCategory={addCategory}
          categories={categories}
          disabled={disabled}
          kind="exclude"
          removeCategory={removeCategory}
          selectedCategoryIds={selectedCategoryIds}
        />
        <div
          data-test-id="category-exclusion-result"
          style={{ borderTop: "1px solid var(--app-border)", paddingTop: "12px" }}
        >
          {t("selected categories")}{" "}
          <strong>
            {selectedCategoryIds.length} {t("categories")}
          </strong>
        </div>
      </div>
      {(selectedAllCategories || allSubCategoriesSelected) && (
        <SystemMessage description={t("You cannot exclude all categories")} level="error" />
      )}
      {(JSON.stringify(currentSelectedIds.sort()) === JSON.stringify(selectedCategoryIds.sort()) &&
        !mutationLoading &&
        !isSaved && <span />) || (
        <div style={{ marginTop: "10px", display: "flex", gap: "8px" }}>
          <Button
            data-test-id="save-category-exclusion"
            disabled={selectedAllCategories || allSubCategoriesSelected}
            variant="action"
            onClick={() => selectCategories()}
          >
            {(isSaved && (
              <span style={{ display: "flex", alignItems: "center" }}>
                <CheckmarkIcon /> &nbsp; {t("Saved")}
              </span>
            )) ||
              (mutationLoading && t("Saving")) ||
              t("Save")}
          </Button>
          {hasUnsavedCategoryChanges && (
            <Button iconLeft="shp-undo" variant="secondary" onClick={() => setSelectedCategoryIds(currentSelectedIds)}>
              {t("Reset to default")}
            </Button>
          )}
        </div>
      )}
      {mutationError && (
        <SystemMessage description={t("there was a problem saving category selection")} level="error" />
      )}
    </>
  );
};

type ExcludeProductsPropsT = {
  adSystem?: AdSystemT;
  isWizard?: boolean;
  onHasUnsavedChanges?: (hasUnsaved: boolean) => void;
  showDashboard?: boolean;
};

export const ExcludeProducts = ({
  adSystem = AdSystemT.GoogleAdsT,
  isWizard = false,
  onHasUnsavedChanges,
  showDashboard = true,
}: ExcludeProductsPropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.AdvancedSetup" });
  const tWizard = useTranslation("translation", { keyPrefix: "Wizard Suggestion Generator Page" }).t;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState("exclude-categories");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getExcludedFieldForFlag = (
    flag: NonNullable<ProductExclusionQueryT["organization"]>["productFlags"][number]
  ) => {
    return adSystem === AdSystemT.MetaAdsT ? flag.excludedMetaAds : flag.excludedGoogleAds;
  };

  const { data, loading } = useProductExclusionQuery({
    variables: { adSystem },
    onCompleted(dataResponse) {
      setSelectedIds(
        dataResponse.organization?.productFlags.filter((flag) => getExcludedFieldForFlag(flag)).map(({ id }) => id) ||
          []
      );
    },
  });

  if (loading || !data?.organization) {
    return <SmallLoader withText />;
  }

  return (
    <div>
      {showDashboard && <ProductsDashboard adSystem={adSystem} />}
      {!isWizard && (
        <div className="typography-no-margin" style={{ margin: "24px 0" }}>
          <H3>{t("Do you want to exclude some products")}</H3>
        </div>
      )}
      <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
        <Button
          data-test-id="exclude-by-category"
          variant={page === "exclude-categories" ? "primary" : "secondary"}
          onClick={() => setPage("exclude-categories")}
        >
          {t("Filter by categories")}
        </Button>
        <Button
          data-test-id="exclude-by-flag"
          variant={page === "exclude-product-flags" ? "primary" : "secondary"}
          onClick={() => setPage("exclude-product-flags")}
        >
          {t("Filter by product flags")}
        </Button>
      </div>
      <div>
        {page === "exclude-categories" && (
          <ExcludeCategoriesSelection
            adSystem={adSystem}
            categories={data.organization.categories}
            onHasUnsavedChanges={onHasUnsavedChanges}
            onOpenModal={() => setIsModalOpen(true)}
          />
        )}
        {page === "exclude-product-flags" && (
          <ExcludeByFlag
            adSystem={adSystem}
            loading={loading}
            organization={data?.organization}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            onHasUnsavedChanges={onHasUnsavedChanges}
            onOpenModal={() => setIsModalOpen(true)}
          />
        )}
      </div>
      <ModalWrapper
        isOpen={isModalOpen}
        title={tWizard("When exclude products modal title")}
        maxWidth
        onClose={() => setIsModalOpen(false)}
      >
        <div style={{ marginBottom: "20px" }}>
          <Text>
            <Trans components={{ b: <b /> }} i18nKey="When exclude products modal text 1" t={tWizard} />
          </Text>
        </div>
        <Trans components={{ b: <b /> }} i18nKey="When exclude products modal text 2" t={tWizard} />
      </ModalWrapper>
    </div>
  );
};
